import {
  Alert,
  Autocomplete,
  FormControl,
  SvgIcon,
  TextField,
} from '@mui/material';
import { RiLoader3Fill } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useContractSelectOptions, {
  ContractSelectItem,
} from '../../../hooks/useContractSelectOptions';
import useForm from '../../../hooks/useForm';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { SelectItem } from '../../../types/core';
import { InvoiceInfo } from '../../../types/invoices';
import getMaterialInputsFormHelper from '../../../utils/getMaterialInputsFormHelper';
import { getMonthName, getValidationErrorMessage } from './utils';
import { useCurrentUser } from '../../../contexts/currentUser';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import FormLayout, {
  FormLayoutInputElement,
} from '../../../components/FormLayout';

export interface ValidateInvoicePayload {
  contractId: string;
  year: string;
  month: string;
}

const InvoiceValidationDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { validateInvoice, getContracts, getAdminDashboardPendingInvoiceInfo } =
    useIotsApi();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { userOperator } = useCurrentUser();

  const {
    data: fetchedContracts = [],
    isFetching: isFetchingContracts,
    error: contractsError,
  } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    select: contracts =>
      [...contracts].sort(
        (a, b) =>
          (a.operatorName ?? '').localeCompare(b.operatorName ?? '') ||
          a.name.localeCompare(b.name),
      ),
  });

  const {
    data: pendingInvoiceInfo = null,
    isFetching: isFetchingPendingInvoices,
    error: pendingInvoicesError,
    refetch: refetchPendingInvoices,
  } = useQuery({
    queryKey: ['pending-invoices'],
    queryFn: () => getAdminDashboardPendingInvoiceInfo(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const formState = useForm({
    initialValues: {
      contractId: '',
      yearAndMonth: '',
    },
    onSubmit: ({ contractId, yearAndMonth }) => {
      // If this is invoked, we can assume it has passed all proper validations
      const [year, month] = yearAndMonth.split('-').map(Number);
      const invoiceInfo: InvoiceInfo = {
        contractId,
        year,
        month: month,
      };
      void navigate(`/admin/invoices/create`, { state: invoiceInfo });
    },
    validate: ({ contractId, yearAndMonth }, setError) => {
      if (!contractId) {
        setError('contractId', t('validation.required-field'));
      }
      if (!yearAndMonth) {
        setError('yearAndMonth', t('validation.required-field'));
      }
    },
  });

  const { contractId, yearAndMonth } = formState.values;

  const {
    data: validations = null,
    isFetching: isFetchingValidations,
    error: validationsError,
  } = useQuery({
    queryKey: ['invoice-validations', contractId, yearAndMonth],
    queryFn: () => {
      const [year, month] = yearAndMonth.split('-');
      if (!contractId || !year || !month) {
        return Promise.resolve(null);
      }
      return validateInvoice(contractId, Number(year), Number(month));
    },
  });

  useQueryErrorNotifier([
    pendingInvoicesError,
    contractsError,
    validationsError,
  ]);

  const { contracts, contractsMap } = useContractSelectOptions(
    fetchedContracts,
    userOperator,
  );

  const { contractYearMonthsMap, isReady } = useMemo(() => {
    const contractYearMonthsMap: Record<string, string[]> = {};

    if (
      isFetchingContracts ||
      isFetchingPendingInvoices ||
      !pendingInvoiceInfo
    ) {
      return { contractYearMonthsMap, isReady: false };
    }

    pendingInvoiceInfo.invoices.forEach(({ contractId, missedInvoices }) => {
      if (!missedInvoices.length) {
        return;
      }

      const contractYearMonths: string[] = [...missedInvoices]
        .sort((a, b) => b.year - a.year || b.month - a.month)
        .map(
          ({ month, year }) => `${year}-${month.toString().padStart(2, '0')}`,
        );
      contractYearMonthsMap[contractId] = contractYearMonths;
    });

    return { contractYearMonthsMap, isReady: true };
  }, [isFetchingContracts, pendingInvoiceInfo, isFetchingPendingInvoices]);

  const { getBaseProps, renderError } = getMaterialInputsFormHelper(formState);

  const { handleSubmit, hasVisibleErrors, values, updateValues } = formState;

  const handleClose = () => {
    onClose();
    formState.reset();
  };

  // Determine selected contract
  const selectedContract = useMemo(() => {
    return contracts.find(contract => contract.value === values.contractId);
  }, [contracts, values.contractId]);

  // Build month/year options dynamically
  const monthYearOptions = useMemo<SelectItem[]>(() => {
    if (!selectedContract) {
      return [];
    }
    const yearMonths = contractYearMonthsMap[selectedContract.value] ?? [];
    return yearMonths.map<SelectItem>(yearMonth => {
      const [year, month] = yearMonth.split('-');
      return {
        value: yearMonth,
        label: `${getMonthName(Number(month) - 1, t)} ${year}`,
      };
    });
  }, [contractYearMonthsMap, selectedContract, t]);

  useEffect(() => {
    if (isOpen) {
      void refetchPendingInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <ConfirmationDialog
      fullWidth
      maxWidth="md"
      open={isOpen}
      onClose={handleClose}
      onConfirm={handleSubmit}
      loading={!isReady || isFetchingValidations}
      submitDisabled={
        !isReady || hasVisibleErrors || (validations?.errors.length ?? 0) > 0
      }
      title={t('content.create-invoice')}
    >
      <p className="mb-4 text-gray-500">
        {t(`content.create-invoice-subtitle`)}
      </p>
      <FormLayout>
        <FormLayoutInputElement required label={t('content.contract')}>
          <FormControl
            error={getBaseProps('contractId').error}
            className="mb-4"
          >
            <Autocomplete<ContractSelectItem>
              id="contract-autocomplete"
              value={contractsMap[values.contractId ?? ''] ?? null}
              onChange={(_event, selectedOption) => {
                updateValues(
                  {
                    contractId: selectedOption?.value ?? '',
                    yearAndMonth: '',
                  },
                  true,
                );
              }}
              options={contracts}
              onBlur={getBaseProps('contractId').onBlur}
              loading={!isReady}
              disabled={getBaseProps('contractId').disabled}
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('contractId').error}
                  placeholder={t('content.select')}
                />
              )}
              getOptionLabel={contract => contract.label}
            />
            {renderError('contractId')}
          </FormControl>
        </FormLayoutInputElement>
        <FormLayoutInputElement required label={t('content.month')}>
          <FormControl error={getBaseProps('yearAndMonth').error}>
            <Autocomplete<SelectItem>
              id="month-autocomplete"
              options={monthYearOptions}
              value={
                monthYearOptions.find(
                  opt => opt.value === values.yearAndMonth,
                ) ?? null
              }
              onChange={(_, selected) => {
                updateValues({ yearAndMonth: selected?.value ?? '' }, true);
              }}
              onBlur={getBaseProps('yearAndMonth').onBlur}
              getOptionLabel={opt => opt?.label ?? ''}
              renderInput={params => (
                <TextField
                  {...params}
                  error={getBaseProps('yearAndMonth').error}
                  placeholder={t('content.select')}
                />
              )}
              disabled={!values.contractId || !isReady}
              noOptionsText={t('content.no-pending-invoices-for-contract')}
            />
            {renderError('yearAndMonth')}
          </FormControl>
        </FormLayoutInputElement>
      </FormLayout>
      {contractId &&
        yearAndMonth &&
        (isFetchingValidations ? (
          <Alert
            severity="info"
            icon={
              <SvgIcon component={RiLoader3Fill} className="animate-spin" />
            }
            className="mt-4"
          >
            {t(
              'content.checking-invoice-creation-validity-for-contract-and-period',
            )}
          </Alert>
        ) : (
          (!validations || validations.errors.length > 0) && (
            <Alert severity="error" className="mt-4">
              {validations ? (
                <ul className="list-disc list-inside">
                  {validations.errors.map((error, index) => (
                    <li key={index}>{getValidationErrorMessage(error, t)}</li>
                  ))}
                </ul>
              ) : (
                <span>
                  {t(
                    'content.checking-invoice-creation-validity-for-contract-and-period-error',
                  )}
                </span>
              )}
            </Alert>
          )
        ))}
    </ConfirmationDialog>
  );
};

export default InvoiceValidationDialog;
